import React, { useCallback, useRef, useState } from 'react';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { api } from '@/api/repository/axios';
import {
    FilterChip,
    FilterChipBar,
    FilterPanel,
    OptionChip,
    OptionGroup,
    OptionSectionTitle,
    SortSelect,
    summarizeSelection,
    useFilterSave,
    useLatestCount,
} from '@/components/common/filter';
import type { FilterPanelControls, FilterSaveControls } from '@/components/common/filter';
import {
    SEMINAR_LEVEL_OPTIONS,
    SEMINAR_SORT_OPTIONS,
    SEMINAR_TYPE_TABS,
    countSeminarConditions,
    formatName,
    formatOptionsForTab,
    parseSavedSeminarQuery,
    savedSeminarParams,
    savedSeminarQueryString,
    seminarQueryKey,
    toSeminarApiFilters,
} from '@/pages/Seminar/seminarListQuery';
import type { SeminarFormatFilter, SeminarListQuery, SeminarOption, SeminarSort, SeminarTypeTab } from '@/pages/Seminar/seminarListQuery';

/**
 * 세미나 목록 조건 칩 줄 — [필터 N] [카테고리] [대상] [진행 방식] [무료만] + 오른쪽 끝 정렬(PC)
 * 칩을 누르면 폰은 아래 시트, PC 는 칩 아래 팝업(공용 FilterPanel). 고른 것은 [세미나 N개 보기]를 눌러야 적용된다.
 * [무료만]은 누르면 바로 켜고 끈다. 정렬은 공용 드롭다운(SortSelect) — 고르면 바로 적용한다.
 */

export interface CategoryGroupOption {
    groupName: string;
    categories: SeminarOption[];
}

type PanelKind = 'all' | 'category' | 'target' | 'format';

interface OpenPanel {
    kind: PanelKind;
    anchor: HTMLElement;
}

const PANEL_TITLES: Record<PanelKind, string> = {
    all: '필터',
    category: '카테고리',
    target: '대상',
    format: '진행 방식',
};

const PANEL_WIDTHS: Record<PanelKind, number> = {
    all: 460,
    category: 420,
    target: 360,
    format: 360,
};

const CAREER_ONLY_FORMAT_HINT: string = '라이브·동영상은 명치사 커리어에만 있어요';
/** 저장 창의 알림 물음 대상 — 세미나 알림은 명치사 커리어만 보낸다(2026-10-01 사용자 결정) */
const CAREER_ALERT_TARGET: string = '명치사 커리어 세미나';

const toggleValue = <T extends string>(list: T[], value: T, order: T[]): T[] =>
    list.includes(value)
        ? list.filter((item: T) => item !== value)
        : order.filter((item: T) => item === value || list.includes(item));

const namesOf = (values: string[], options: SeminarOption[]): string[] =>
    values
        .map((value: string) => options.find((option: SeminarOption) => option.value === value)?.name)
        .filter((name: string | undefined): name is string => Boolean(name));

interface SeminarFilterPanelProps {
    kind: PanelKind;
    anchor: HTMLElement;
    query: SeminarListQuery;
    categoryGroups: CategoryGroupOption[];
    onClose: () => void;
    onApply: (next: SeminarListQuery) => void;
}

/** 열려 있는 동안만 그려진다 — 고르던 상태(초안)와 결과 수 세기는 창이 열릴 때 시작해 닫히면 버린다 */
const SeminarFilterPanel = ({ kind, anchor, query, categoryGroups, onClose, onApply }: SeminarFilterPanelProps) => {
    const [draft, setDraftState] = useState<SeminarListQuery>(query);
    const draftRef = useRef<SeminarListQuery>(query);
    const setDraft = (next: SeminarListQuery): void => {
        draftRef.current = next;
        setDraftState(next);
    };

    const categoryOrder: string[] = categoryGroups.flatMap((group: CategoryGroupOption) => group.categories.map((category: SeminarOption) => category.value));
    const levelOrder: string[] = SEMINAR_LEVEL_OPTIONS.map((option: SeminarOption) => option.value);
    const formatOptions: SeminarOption<SeminarFormatFilter>[] = formatOptionsForTab(draft.tab);
    const formatOrder: SeminarFormatFilter[] = formatOptions.map((option: SeminarOption<SeminarFormatFilter>) => option.value);

    // 정렬은 결과 수와 무관 — 세는 조건에서 뺀다
    const countKey: string = seminarQueryKey({ ...draft, sort: 'latest' });
    const fetchCount = useCallback(async (): Promise<number> => {
        const response = await api.seminar.getList(1, 0, toSeminarApiFilters(draftRef.current));
        if (response.code !== 0) throw new Error('count failed');
        return Number(response.result?.total ?? 0);
    }, []);
    const { count } = useLatestCount(fetchCount, countKey);

    /** 이 조건 창을 닫으며 적용하는 길 — 저장한 뒤 그 조건을 목록에 바로 적용할 때 쓴다 */
    const panelControlsRef = useRef<FilterPanelControls | null>(null);
    // 조건 저장(모든 창의 제목 줄) — 어느 창에서 누르든 지금 조건 전체를 탭까지 저장한다.
    // 알림은 명치사 커리어 세미나만 가므로 일반 탭에서는 묻지 않는다
    const flatCategories: SeminarOption[] = categoryGroups.flatMap((group: CategoryGroupOption) => group.categories);
    const saveSummary: string[] = [
        SEMINAR_TYPE_TABS.find((tab: { key: SeminarTypeTab; label: string }) => tab.key === draft.tab)?.label ?? '전체',
        ...[
            namesOf(draft.categories, flatCategories),
            namesOf(draft.levels, SEMINAR_LEVEL_OPTIONS),
            draft.formats.map(formatName),
        ].filter((names: string[]) => names.length > 0).map((names: string[]) => summarizeSelection(names, '')),
        ...(draft.freeOnly ? ['무료만'] : []),
    ];
    const filterSave: FilterSaveControls = useFilterSave({
        domain: 'seminar',
        draftQuery: savedSeminarQueryString(draft),
        // 세미나의 탭(전체·커리어·일반)은 결과가 달라지는 조건이라 함께 견준다
        isSameAsSaved: (savedQuery: string) => savedSeminarQueryString(parseSavedSeminarQuery(savedQuery)) === savedSeminarQueryString(draft),
        getParams: () => savedSeminarParams(draftRef.current),
        summary: saveSummary,
        onSaved: () => panelControlsRef.current?.apply(),
        alertTarget: draft.tab === 'general' ? undefined : CAREER_ALERT_TARGET,
    });

    const applyLabel: string = count === null
        ? '세미나 보기'
        : count === 0
            ? '조건에 맞는 세미나가 없어요'
            : `세미나 ${count.toLocaleString()}개 보기`;

    const reset = (): void => {
        if (kind === 'category') setDraft({ ...draft, categories: [] });
        else if (kind === 'target') setDraft({ ...draft, levels: [] });
        else if (kind === 'format') setDraft({ ...draft, formats: [] });
        else setDraft({ ...draft, categories: [], levels: [], formats: [], freeOnly: false });
    };

    const categoryGroupsView = (strongTitle: boolean): React.ReactNode => (
        <>
            {strongTitle && <OptionSectionTitle>카테고리</OptionSectionTitle>}
            {categoryGroups.map((group: CategoryGroupOption) => (
                <OptionGroup key={group.groupName} label={group.groupName}>
                    {group.categories.map((category: SeminarOption) => (
                        <OptionChip
                            key={category.value}
                            selected={draft.categories.includes(category.value)}
                            onClick={() => setDraft({ ...draft, categories: toggleValue(draft.categories, category.value, categoryOrder) })}
                        >
                            {category.name}
                        </OptionChip>
                    ))}
                </OptionGroup>
            ))}
        </>
    );

    const levelView = (label?: string): React.ReactNode => (
        <OptionGroup label={label} strong={Boolean(label)}>
            {SEMINAR_LEVEL_OPTIONS.map((level: SeminarOption) => (
                <OptionChip
                    key={level.value}
                    selected={draft.levels.includes(level.value)}
                    onClick={() => setDraft({ ...draft, levels: toggleValue(draft.levels, level.value, levelOrder) })}
                >
                    {level.name}
                </OptionChip>
            ))}
        </OptionGroup>
    );

    const formatView = (label?: string): React.ReactNode => (
        <OptionGroup label={label} strong={Boolean(label)} hint={draft.tab === 'all' ? CAREER_ONLY_FORMAT_HINT : undefined}>
            {formatOptions.map((option: SeminarOption<SeminarFormatFilter>) => (
                <OptionChip
                    key={option.value}
                    selected={draft.formats.includes(option.value)}
                    onClick={() => setDraft({ ...draft, formats: toggleValue(draft.formats, option.value, formatOrder) })}
                >
                    {option.name}
                </OptionChip>
            ))}
        </OptionGroup>
    );

    const renderBody = (): React.ReactNode => {
        switch (kind) {
            case 'category':
                return categoryGroupsView(false);
            case 'target':
                return <div style={{ paddingTop: 12 }}>{levelView()}</div>;
            case 'format':
                return <div style={{ paddingTop: 12 }}>{formatView()}</div>;
            default:
                return (
                    <>
                        {categoryGroupsView(true)}
                        {levelView('대상')}
                        {formatView('진행 방식')}
                        <OptionGroup label="가격" strong>
                            <OptionChip selected={draft.freeOnly} onClick={() => setDraft({ ...draft, freeOnly: !draft.freeOnly })}>
                                무료만
                            </OptionChip>
                        </OptionGroup>
                    </>
                );
        }
    };

    return (
        <>
            <FilterPanel
                open
                anchorEl={anchor}
                title={PANEL_TITLES[kind]}
                // 모든 창의 제목 줄에 조건 저장 버튼을 둔다(안내 문구 대신)
                headerAction={filterSave.headerAction}
                popoverWidth={PANEL_WIDTHS[kind]}
                onClose={onClose}
                onApply={() => onApply(draftRef.current)}
                onReset={reset}
                applyLabel={applyLabel}
                applyDisabled={count === 0}
            >
                {(controls: FilterPanelControls) => {
                    panelControlsRef.current = controls;
                    return renderBody();
                }}
            </FilterPanel>
            {filterSave.savePanel}
        </>
    );
};

export interface SeminarFilterBarProps {
    query: SeminarListQuery;
    categoryGroups: CategoryGroupOption[];
    /** PC: 무료만 칩 + 줄 오른쪽 끝 글자형 정렬 / 폰: 둘 다 화면이 본문 맨 위 조작 줄에 따로 그린다 */
    isMobile: boolean;
    onApply: (next: SeminarListQuery) => void;
    className?: string;
}

const SeminarFilterBar = ({ query, categoryGroups, isMobile, onApply, className }: SeminarFilterBarProps) => {
    const [panel, setPanel] = useState<OpenPanel | null>(null);

    const toggle = (kind: PanelKind) => (event: React.MouseEvent<HTMLButtonElement>): void => {
        const anchor: HTMLElement = event.currentTarget;
        setPanel((previous: OpenPanel | null) => (previous?.kind === kind ? null : { kind, anchor }));
    };
    const close = (): void => setPanel(null);
    const apply = (next: SeminarListQuery): void => {
        setPanel(null);
        onApply(next);
    };

    const flatCategories: SeminarOption[] = categoryGroups.flatMap((group: CategoryGroupOption) => group.categories);
    const conditionCount: number = countSeminarConditions(query);
    const isOpen = (kind: PanelKind): boolean => panel?.kind === kind;

    const sortSelect: React.ReactNode = (
        <SortSelect options={SEMINAR_SORT_OPTIONS} value={query.sort} defaultValue="latest" onChange={(sort: SeminarSort) => onApply({ ...query, sort })} />
    );

    return (
        <>
            <FilterChipBar ariaLabel="세미나 조건" className={className} end={isMobile ? undefined : sortSelect}>
                <FilterChip
                    label="필터"
                    ariaLabel={conditionCount > 0 ? `필터 ${conditionCount}개 적용됨` : '필터'}
                    icon={<TuneRoundedIcon aria-hidden="true" />}
                    badge={conditionCount}
                    selected={conditionCount > 0}
                    hasPanel
                    arrow={false}
                    open={isOpen('all')}
                    onClick={toggle('all')}
                />
                <FilterChip
                    label={summarizeSelection(namesOf(query.categories, flatCategories), '카테고리')}
                    selected={query.categories.length > 0}
                    hasPanel
                    open={isOpen('category')}
                    onClick={toggle('category')}
                />
                <FilterChip
                    label={summarizeSelection(namesOf(query.levels, SEMINAR_LEVEL_OPTIONS), '대상')}
                    selected={query.levels.length > 0}
                    hasPanel
                    open={isOpen('target')}
                    onClick={toggle('target')}
                />
                <FilterChip
                    label={summarizeSelection(query.formats.map(formatName), '진행 방식')}
                    selected={query.formats.length > 0}
                    hasPanel
                    open={isOpen('format')}
                    onClick={toggle('format')}
                />
                {/* 폰은 무료만·정렬을 본문 맨 위 조작 줄(ListControlRow)에 둔다 — 칩 줄에는 PC 만 */}
                {!isMobile && (
                    <FilterChip
                        label="무료만"
                        toggle
                        selected={query.freeOnly}
                        onClick={() => onApply({ ...query, freeOnly: !query.freeOnly })}
                    />
                )}
            </FilterChipBar>
            {panel && (
                <SeminarFilterPanel
                    key={panel.kind}
                    kind={panel.kind}
                    anchor={panel.anchor}
                    query={query}
                    categoryGroups={categoryGroups}
                    onClose={close}
                    onApply={apply}
                />
            )}
        </>
    );
};

export default SeminarFilterBar;
